import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";

// ✅ USE CENTRALIZED AXIOS CONFIG
import api from "../../utills/api";


/* =========================================================
   AUTH HELPERS
========================================================= */

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    "";

  return {
    "Content-Type": "application/json",
    ...(token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {}),
  };
};


/* =========================================================
   HELPERS
========================================================= */

const getLeadName = (lead) => {
  if (!lead) return "Unknown Lead";

  return (
    lead.name ||
    [lead.firstName, lead.lastName]
      .filter(Boolean)
      .join(" ") ||
    "Unknown Lead"
  );
};


const formatDate = (value, withTime = false) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  });
};


const toInputDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const pad = (number) =>
    String(number).padStart(2, "0");

  return `${date.getFullYear()}-${pad(
    date.getMonth() + 1
  )}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};


const getStatusClass = (status) => {
  switch (status) {
    case "Pending":
      return "bg-warning-subtle text-warning";

    case "Completed":
      return "bg-success-subtle text-success";

    case "Cancelled":
      return "bg-secondary-subtle text-secondary";

    case "Overdue":
      return "bg-danger-subtle text-danger";

    default:
      return "bg-light text-muted";
  }
};


const getPriorityClass = (priority) => {
  switch (priority) {
    case "High":
      return "bg-danger-subtle text-danger";

    case "Medium":
      return "bg-warning-subtle text-warning";

    case "Low":
      return "bg-success-subtle text-success";

    default:
      return "bg-light text-muted";
  }
};


const getFollowUpDate = (item) => {
  return (
    item.followUpDate ||
    item.scheduledAt ||
    item.nextFollowUpAt ||
    item.date ||
    item.dueAt ||
    null
  );
};


const getFollowUpStatus = (item) => {
  if (item.status) return item.status;

  const date = getFollowUpDate(item);

  if (!date) return "Pending";

  if (new Date(date).getTime() < Date.now()) {
    return "Overdue";
  }

  return "Pending";
};


const normalizeFollowUp = (item) => {
  const lead =
    item.lead ||
    item.leadId ||
    item.educationLead ||
    null;

  return {
    ...item,

    lead,

    leadName:
      item.leadName ||
      getLeadName(lead) ||
      "Unknown Lead",

    phone:
      item.phone ||
      lead?.mobile ||
      lead?.phone ||
      "",

    email:
      item.email ||
      lead?.email ||
      "",

    followUpDate:
      getFollowUpDate(item),

    status:
      getFollowUpStatus(item),

    priority:
      item.priority ||
      lead?.priority ||
      "Medium",

    type:
      item.type ||
      item.followUpType ||
      item.method ||
      "Call",
  };
};


/* =========================================================
   COMPONENT
========================================================= */

const FollowUps = () => {
  const navigate = useNavigate();

  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] =
    useState("today");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState("");
  const [priorityFilter, setPriorityFilter] =
    useState("");

  const [modalOpen, setModalOpen] =
    useState(false);

  const [editingFollowUp, setEditingFollowUp] =
    useState(null);

  const [deleteModal, setDeleteModal] =
    useState(false);

  const [selectedFollowUp, setSelectedFollowUp] =
    useState(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);


  /* =========================================================
     FETCH FOLLOW-UPS
  ========================================================= */

  const fetchFollowUps = useCallback(
    async () => {
      setLoading(true);

      try {
        // ✅ Axios GET
        const response = await api.get(
          "/follow-ups",
          {
            headers: getAuthHeaders(),
          }
        );

        // ✅ Axios response data
        const result = response.data;

        const data =
          result?.data || {};

        const rows = Array.isArray(data)
          ? data
          : data.followUps ||
            data.items ||
            data.rows ||
            [];

        setFollowUps(
          rows.map(normalizeFollowUp)
        );
      } catch (error) {
        console.error(
          "Follow-ups error:",
          error
        );

        // Axios backend error message
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load follow-ups.";

        toast.error(message);

        setFollowUps([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );


  useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps]);


  /* =========================================================
     DATE RANGES
  ========================================================= */

  const todayStart = useMemo(() => {
    const date = new Date();

    date.setHours(0, 0, 0, 0);

    return date;
  }, []);


  const tomorrowStart = useMemo(() => {
    const date = new Date(todayStart);

    date.setDate(
      date.getDate() + 1
    );

    return date;
  }, [todayStart]);


  const weekEnd = useMemo(() => {
    const date = new Date(todayStart);

    date.setDate(
      date.getDate() + 7
    );

    return date;
  }, [todayStart]);


  /* =========================================================
     CATEGORIZATION
  ========================================================= */

  const categorized = useMemo(() => {
    const today = [];
    const overdue = [];
    const upcoming = [];
    const completed = [];

    followUps.forEach((item) => {
      const status = item.status;

      const dateValue =
        getFollowUpDate(item);

      const date = dateValue
        ? new Date(dateValue)
        : null;

      if (
        status === "Completed" ||
        item.completedAt
      ) {
        completed.push(item);
        return;
      }

      if (
        status === "Cancelled"
      ) {
        return;
      }

      if (
        date &&
        date < todayStart
      ) {
        overdue.push(item);
        return;
      }

      if (
        date &&
        date >= todayStart &&
        date < tomorrowStart
      ) {
        today.push(item);
        return;
      }

      if (
        date &&
        date >= tomorrowStart &&
        date <= weekEnd
      ) {
        upcoming.push(item);
        return;
      }

      if (!date) {
        upcoming.push(item);
      }
    });

    return {
      today,
      overdue,
      upcoming,
      completed,
    };
  }, [
    followUps,
    todayStart,
    tomorrowStart,
    weekEnd,
  ]);


  /* =========================================================
     FILTERING
  ========================================================= */

  const currentRows = useMemo(() => {
    let rows =
      categorized[activeTab] || [];

    const query =
      search.trim().toLowerCase();

    if (query) {
      rows = rows.filter((item) => {
        return [
          item.leadName,
          item.phone,
          item.email,
          item.type,
          item.notes,
          item.description,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value)
              .toLowerCase()
              .includes(query)
          );
      });
    }

    if (statusFilter) {
      rows = rows.filter(
        (item) =>
          item.status === statusFilter
      );
    }

    if (priorityFilter) {
      rows = rows.filter(
        (item) =>
          item.priority ===
          priorityFilter
      );
    }

    return rows;
  }, [
    categorized,
    activeTab,
    search,
    statusFilter,
    priorityFilter,
  ]);


  /* =========================================================
     MODAL
  ========================================================= */

  const openAddModal = () => {
    setEditingFollowUp(null);
    setModalOpen(true);
  };


  const openEditModal = (item) => {
    setEditingFollowUp(item);
    setModalOpen(true);
  };


  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingFollowUp(null);
  };


  /* =========================================================
     FORMIK
  ========================================================= */

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      leadId:
        editingFollowUp?.lead?._id ||
        editingFollowUp?.leadId?._id ||
        editingFollowUp?.leadId ||
        "",

      followUpDate:
        toInputDateTime(
          editingFollowUp?.scheduledAt ||
            editingFollowUp?.followUpDate
        ),

      type:
        editingFollowUp?.type ||
        "Call",

      priority:
        editingFollowUp?.priority ||
        "Medium",

      notes:
        editingFollowUp?.notes ||
        editingFollowUp?.description ||
        "",

      status:
        editingFollowUp?.status ===
        "Completed"
          ? "Completed"
          : "Pending",
    },

    validationSchema: Yup.object({
      leadId: Yup.string().required(
        "Lead is required"
      ),

      followUpDate: Yup.string().required(
        "Follow-up date is required"
      ),

      type: Yup.string().required(
        "Follow-up type is required"
      ),

      priority: Yup.string().required(
        "Priority is required"
      ),
    }),


    /* =========================================================
       CREATE / UPDATE FOLLOW-UP
    ========================================================= */

    onSubmit: async (values) => {
      setSaving(true);

      try {
        const payload = {
          leadId: values.leadId,
          scheduledAt:
            values.followUpDate,
          type: values.type,
          priority: values.priority,
          notes: values.notes,
          status: values.status,
        };

        const isEdit =
          Boolean(
            editingFollowUp?._id
          );


        let response;

        if (isEdit) {
          // ✅ Axios PUT
          response = await api.put(
            `/follow-ups/${editingFollowUp._id}`,
            payload,
            {
              headers:
                getAuthHeaders(),
            }
          );
        } else {
          // ✅ Axios POST
          response = await api.post(
            "/follow-ups",
            payload,
            {
              headers:
                getAuthHeaders(),
            }
          );
        }


        // ✅ Axios response
        const result =
          response.data;


        toast.success(
          isEdit
            ? "Follow-up updated successfully."
            : "Follow-up created successfully."
        );


        closeModal();

        await fetchFollowUps();
      } catch (error) {
        console.error(
          "Save follow-up error:",
          error
        );

        const message =
          error?.response?.data?.message ||
          error?.message ||
          `Unable to ${
            editingFollowUp
              ? "update"
              : "create"
          } follow-up.`;

        toast.error(message);
      } finally {
        setSaving(false);
      }
    },
  });


  /* =========================================================
     COMPLETE FOLLOW-UP
  ========================================================= */

  const handleComplete = async (
    item
  ) => {
    if (!item?._id) return;

    try {
      // ✅ Axios PUT
      await api.put(
        `/follow-ups/${item._id}`,
        {
          status: "Completed",
          completedAt:
            new Date().toISOString(),
        },
        {
          headers:
            getAuthHeaders(),
        }
      );


      toast.success(
        "Follow-up marked as completed."
      );


      await fetchFollowUps();
    } catch (error) {
      console.error(
        "Complete follow-up error:",
        error
      );

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to complete follow-up.";

      toast.error(message);
    }
  };


  /* =========================================================
     DELETE FOLLOW-UP
  ========================================================= */

  const handleDelete = async () => {
    if (!selectedFollowUp?._id) {
      return;
    }

    setDeleting(true);

    try {
      // ✅ Axios DELETE
      await api.delete(
        `/follow-ups/${selectedFollowUp._id}`,
        {
          headers:
            getAuthHeaders(),
        }
      );


      toast.success(
        "Follow-up deleted successfully."
      );


      setDeleteModal(false);
      setSelectedFollowUp(null);

      await fetchFollowUps();
    } catch (error) {
      console.error(
        "Delete follow-up error:",
        error
      );

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to delete follow-up.";

      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };


  /* =========================================================
     COUNT
  ========================================================= */

  const renderCount = (key) =>
    categorized[key]?.length || 0;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Follow-ups"
            pageTitle="Leads"
          />

          {/* Header */}
          <Row className="mb-3">
            <Col>
              <Card className="mb-0">
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div>
                      <h4 className="mb-1">
                        Follow-ups
                      </h4>

                      <p className="text-muted mb-0">
                        Manage scheduled,
                        overdue and completed
                        lead follow-ups.
                      </p>
                    </div>

                    <Button
                      color="primary"
                      onClick={
                        openAddModal
                      }
                    >
                      <i className="ri-add-line me-1" />
                      Schedule Follow-up
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Summary Cards */}
          <Row>
            <Col xl={3} md={6}>
              <Card
                className="card-animate cursor-pointer"
                onClick={() =>
                  setActiveTab("today")
                }
              >
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="fw-medium text-muted mb-0">
                        Today
                      </p>

                      <h3 className="mt-2 mb-0">
                        {renderCount(
                          "today"
                        )}
                      </h3>
                    </div>

                    <div className="avatar-sm">
                      <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-22">
                        <i className="ri-calendar-check-line" />
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card
                className="card-animate cursor-pointer"
                onClick={() =>
                  setActiveTab("overdue")
                }
              >
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="fw-medium text-muted mb-0">
                        Overdue
                      </p>

                      <h3 className="mt-2 mb-0">
                        {renderCount(
                          "overdue"
                        )}
                      </h3>
                    </div>

                    <div className="avatar-sm">
                      <div className="avatar-title bg-danger-subtle text-danger rounded-circle fs-22">
                        <i className="ri-alarm-warning-line" />
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card
                className="card-animate cursor-pointer"
                onClick={() =>
                  setActiveTab("upcoming")
                }
              >
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="fw-medium text-muted mb-0">
                        Upcoming
                      </p>

                      <h3 className="mt-2 mb-0">
                        {renderCount(
                          "upcoming"
                        )}
                      </h3>
                    </div>

                    <div className="avatar-sm">
                      <div className="avatar-title bg-info-subtle text-info rounded-circle fs-22">
                        <i className="ri-calendar-event-line" />
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card
                className="card-animate cursor-pointer"
                onClick={() =>
                  setActiveTab("completed")
                }
              >
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="fw-medium text-muted mb-0">
                        Completed
                      </p>

                      <h3 className="mt-2 mb-0">
                        {renderCount(
                          "completed"
                        )}
                      </h3>
                    </div>

                    <div className="avatar-sm">
                      <div className="avatar-title bg-success-subtle text-success rounded-circle fs-22">
                        <i className="ri-checkbox-circle-line" />
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Main */}
          <Card>
            <CardHeader>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="nav-tabs-custom">
                  <ul className="nav nav-tabs nav-tabs-custom nav-success mb-0">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${
                          activeTab ===
                          "today"
                            ? "active"
                            : ""
                        }`}
                        onClick={() =>
                          setActiveTab(
                            "today"
                          )
                        }
                      >
                        Today
                        <Badge
                          color="primary"
                          className="ms-1"
                        >
                          {renderCount(
                            "today"
                          )}
                        </Badge>
                      </button>
                    </li>

                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${
                          activeTab ===
                          "overdue"
                            ? "active"
                            : ""
                        }`}
                        onClick={() =>
                          setActiveTab(
                            "overdue"
                          )
                        }
                      >
                        Overdue
                        <Badge
                          color="danger"
                          className="ms-1"
                        >
                          {renderCount(
                            "overdue"
                          )}
                        </Badge>
                      </button>
                    </li>

                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${
                          activeTab ===
                          "upcoming"
                            ? "active"
                            : ""
                        }`}
                        onClick={() =>
                          setActiveTab(
                            "upcoming"
                          )
                        }
                      >
                        Upcoming
                        <Badge
                          color="info"
                          className="ms-1"
                        >
                          {renderCount(
                            "upcoming"
                          )}
                        </Badge>
                      </button>
                    </li>

                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${
                          activeTab ===
                          "completed"
                            ? "active"
                            : ""
                        }`}
                        onClick={() =>
                          setActiveTab(
                            "completed"
                          )
                        }
                      >
                        Completed
                        <Badge
                          color="success"
                          className="ms-1"
                        >
                          {renderCount(
                            "completed"
                          )}
                        </Badge>
                      </button>
                    </li>
                  </ul>
                </div>

                <Button
                  color="soft-primary"
                  onClick={
                    fetchFollowUps
                  }
                >
                  <i className="ri-refresh-line me-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>

            <CardBody>
              {/* Filters */}
              <Row className="g-3 mb-4">
                <Col lg={5}>
                  <div className="search-box">
                    <Input
                      type="text"
                      className="form-control search"
                      placeholder="Search lead, phone, email..."
                      value={search}
                      onChange={(e) =>
                        setSearch(
                          e.target.value
                        )
                      }
                    />

                    <i className="ri-search-line search-icon" />
                  </div>
                </Col>

                <Col lg={3} md={4}>
                  <select
                    className="form-select"
                    value={
                      statusFilter
                    }
                    onChange={(e) =>
                      setStatusFilter(
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      All Statuses
                    </option>
                    <option value="Pending">
                      Pending
                    </option>
                    <option value="Completed">
                      Completed
                    </option>
                    <option value="Cancelled">
                      Cancelled
                    </option>
                    <option value="Overdue">
                      Overdue
                    </option>
                  </select>
                </Col>

                <Col lg={3} md={4}>
                  <select
                    className="form-select"
                    value={
                      priorityFilter
                    }
                    onChange={(e) =>
                      setPriorityFilter(
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      All Priorities
                    </option>
                    <option value="High">
                      High
                    </option>
                    <option value="Medium">
                      Medium
                    </option>
                    <option value="Low">
                      Low
                    </option>
                  </select>
                </Col>

                <Col lg={1} md={4}>
                  <Button
                    color="light"
                    className="w-100"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter(
                        ""
                      );
                      setPriorityFilter(
                        ""
                      );
                    }}
                  >
                    <i className="ri-filter-off-line" />
                  </Button>
                </Col>
              </Row>

              {/* Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner
                    color="primary"
                    className="mb-3"
                  />

                  <h6>
                    Loading follow-ups...
                  </h6>
                </div>
              ) : currentRows.length ===
                0 ? (
                <div className="text-center py-5">
                  <div className="avatar-lg mx-auto mb-3">
                    <div className="avatar-title bg-light text-muted rounded-circle fs-24">
                      <i className="ri-calendar-line" />
                    </div>
                  </div>

                  <h5>
                    No follow-ups found
                  </h5>

                  <p className="text-muted mb-3">
                    There are no follow-ups
                    matching the current
                    filters.
                  </p>

                  <Button
                    color="primary"
                    onClick={
                      openAddModal
                    }
                  >
                    <i className="ri-add-line me-1" />
                    Schedule Follow-up
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Lead</th>
                        <th>Follow-up</th>
                        <th>Type</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th className="text-end">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentRows.map(
                        (item) => (
                          <tr
                            key={
                              item._id
                            }
                          >
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-xs me-2">
                                  <div className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                    {item.leadName
                                      ?.charAt(
                                        0
                                      )
                                      ?.toUpperCase() ||
                                      "L"}
                                  </div>
                                </div>

                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-body fw-medium"
                                    onClick={() =>
                                      item
                                        .lead
                                        ? navigate(
                                            `/leads/${item.lead._id}`
                                          )
                                        : null
                                    }
                                  >
                                    {
                                      item.leadName
                                    }
                                  </button>

                                  {item.phone && (
                                    <div className="text-muted fs-12">
                                      {
                                        item.phone
                                      }
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td>
                              <div className="fw-medium">
                                {formatDate(
                                  item.followUpDate,
                                  true
                                )}
                              </div>
                            </td>

                            <td>
                              <span>
                                <i
                                  className={`me-1 ${
                                    item.type ===
                                    "Email"
                                      ? "ri-mail-line"
                                      : item.type ===
                                        "WhatsApp"
                                      ? "ri-whatsapp-line"
                                      : item.type ===
                                        "Meeting"
                                      ? "ri-calendar-event-line"
                                      : "ri-phone-line"
                                  }`}
                                />

                                {item.type}
                              </span>
                            </td>

                            <td>
                              <Badge
                                className={`badge-label ${getPriorityClass(
                                  item.priority
                                )}`}
                              >
                                {
                                  item.priority
                                }
                              </Badge>
                            </td>

                            <td>
                              <Badge
                                className={`badge-label ${getStatusClass(
                                  item.status
                                )}`}
                              >
                                {
                                  item.status
                                }
                              </Badge>
                            </td>

                            <td>
                              <span
                                className="text-muted d-inline-block text-truncate"
                                style={{
                                  maxWidth:
                                    "220px",
                                }}
                              >
                                {item.notes ||
                                  item.description ||
                                  "—"}
                              </span>
                            </td>

                            <td className="text-end">
                              <div className="d-flex justify-content-end gap-1">
                                {item.status !==
                                  "Completed" && (
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="btn-icon"
                                    title="Mark completed"
                                    onClick={() =>
                                      handleComplete(
                                        item
                                      )
                                    }
                                  >
                                    <i className="ri-check-line" />
                                  </Button>
                                )}

                                <Button
                                  color="light"
                                  size="sm"
                                  className="btn-icon"
                                  title="Edit"
                                  onClick={() =>
                                    openEditModal(
                                      item
                                    )
                                  }
                                >
                                  <i className="ri-pencil-line" />
                                </Button>

                                <Button
                                  color="light"
                                  size="sm"
                                  className="btn-icon text-danger"
                                  title="Delete"
                                  onClick={() => {
                                    setSelectedFollowUp(
                                      item
                                    );
                                    setDeleteModal(
                                      true
                                    );
                                  }}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Add / Edit Follow-up Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        centered
        size="lg"
      >
        <ModalHeader toggle={closeModal}>
          {editingFollowUp
            ? "Edit Follow-up"
            : "Schedule Follow-up"}
        </ModalHeader>

        <Form
          onSubmit={
            formik.handleSubmit
          }
        >
          <ModalBody>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label>
                    Lead{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <Input
                    type="text"
                    name="leadId"
                    placeholder="Enter Lead ID"
                    value={
                      formik.values.leadId
                    }
                    onChange={
                      formik.handleChange
                    }
                    onBlur={
                      formik.handleBlur
                    }
                    invalid={
                      formik.touched
                        .leadId &&
                      Boolean(
                        formik.errors
                          .leadId
                      )
                    }
                    disabled={
                      Boolean(
                        editingFollowUp
                      )
                    }
                  />

                  {formik.touched
                    .leadId &&
                    formik.errors
                      .leadId && (
                      <FormFeedback>
                        {
                          formik.errors
                            .leadId
                        }
                      </FormFeedback>
                    )}

                  {!editingFollowUp && (
                    <div className="text-muted fs-11 mt-1">
                      Use the Education Lead
                      ID for the lead.
                    </div>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <Label>
                    Follow-up Date & Time{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <Input
                    type="datetime-local"
                    name="followUpDate"
                    value={
                      formik.values
                        .followUpDate
                    }
                    onChange={
                      formik.handleChange
                    }
                    onBlur={
                      formik.handleBlur
                    }
                    invalid={
                      formik.touched
                        .followUpDate &&
                      Boolean(
                        formik.errors
                          .followUpDate
                      )
                    }
                  />

                  {formik.touched
                    .followUpDate &&
                    formik.errors
                      .followUpDate && (
                      <FormFeedback>
                        {
                          formik.errors
                            .followUpDate
                        }
                      </FormFeedback>
                    )}
                </div>
              </Col>

              <Col md={4}>
                <div className="mb-3">
                  <Label>
                    Follow-up Type
                  </Label>

                  <select
                    className="form-select"
                    name="type"
                    value={
                      formik.values.type
                    }
                    onChange={
                      formik.handleChange
                    }
                  >
                    <option value="Call">
                      Call
                    </option>

                    <option value="WhatsApp">
                      WhatsApp
                    </option>

                    <option value="Email">
                      Email
                    </option>

                    <option value="Meeting">
                      Meeting
                    </option>

                    <option value="Counselling">
                      Counselling
                    </option>

                    <option value="Visit">
                      Campus Visit
                    </option>

                    <option value="Other">
                      Other
                    </option>
                  </select>
                </div>
              </Col>

              <Col md={4}>
                <div className="mb-3">
                  <Label>
                    Priority
                  </Label>

                  <select
                    className="form-select"
                    name="priority"
                    value={
                      formik.values
                        .priority
                    }
                    onChange={
                      formik.handleChange
                    }
                  >
                    <option value="High">
                      High
                    </option>

                    <option value="Medium">
                      Medium
                    </option>

                    <option value="Low">
                      Low
                    </option>
                  </select>
                </div>
              </Col>

              <Col md={4}>
                <div className="mb-3">
                  <Label>
                    Status
                  </Label>

                  <select
                    className="form-select"
                    name="status"
                    value={
                      formik.values.status
                    }
                    onChange={
                      formik.handleChange
                    }
                  >
                    <option value="Pending">
                      Pending
                    </option>

                    <option value="Completed">
                      Completed
                    </option>
                  </select>
                </div>
              </Col>

              <Col xs={12}>
                <div className="mb-3">
                  <Label>
                    Notes
                  </Label>

                  <Input
                    type="textarea"
                    rows="4"
                    name="notes"
                    placeholder="Add notes about this follow-up..."
                    value={
                      formik.values.notes
                    }
                    onChange={
                      formik.handleChange
                    }
                  />
                </div>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="light"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-1"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1" />
                  {editingFollowUp
                    ? "Update Follow-up"
                    : "Schedule Follow-up"}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={
          handleDelete
        }
        onCloseClick={() => {
          if (!deleting) {
            setDeleteModal(false);
            setSelectedFollowUp(
              null
            );
          }
        }}
        isDeleting={deleting}
      />

      <ToastContainer closeButton={false} />
    </React.Fragment>
  );
};

export default FollowUps;